import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * Popover menu for inline row actions. Closes on outside click and Esc.
 * `placement="auto"` flips above the trigger when there is not enough space below.
 */
interface MenuProps {
  trigger: React.ReactNode
  triggerAriaLabel: string
  align?: 'left' | 'right'
  placement?: 'auto' | 'below' | 'above'
  className?: string
  triggerClassName?: string
  /** Tighter padding and type — for dense toolbars and list rows. */
  dense?: boolean
  /** Render in a portal to escape overflow clipping (e.g. tables). */
  portal?: boolean
  children: (close: () => void) => React.ReactNode
}

const MenuDensityContext = createContext(false)

const Menu: React.FC<MenuProps> = ({
  trigger,
  triggerAriaLabel,
  align = 'right',
  placement = 'auto',
  className = '',
  triggerClassName = '',
  dense = false,
  portal = false,
  children,
}) => {
  const [open, setOpen] = useState(false)
  const [placeAbove, setPlaceAbove] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const syncPosition = () => {
    if (!open || !wrapRef.current) return

    const triggerRect = wrapRef.current.getBoundingClientRect()
    const menuHeight = menuRef.current?.getBoundingClientRect().height ?? 0

    let above = false
    if (placement === 'above') {
      above = true
    } else if (placement === 'below') {
      above = false
    } else if (menuHeight > 0) {
      const spaceBelow = window.innerHeight - triggerRect.bottom
      const spaceAbove = triggerRect.top
      const needed = menuHeight + 8
      above = spaceBelow < needed && spaceAbove >= needed
    }

    setPlaceAbove(above)

    if (portal) {
      setMenuPos({
        top:
          above && menuHeight > 0
            ? triggerRect.top - menuHeight - 4
            : triggerRect.bottom + 4,
        left: align === 'right' ? triggerRect.right : triggerRect.left,
      })
    }
  }

  const toggleOpen = () => {
    setOpen((wasOpen) => {
      if (wasOpen) {
        setMenuPos(null)
        return false
      }
      if (portal && wrapRef.current) {
        const rect = wrapRef.current.getBoundingClientRect()
        setMenuPos({
          top: rect.bottom + 4,
          left: align === 'right' ? rect.right : rect.left,
        })
      }
      return true
    })
  }

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null)
      setPlaceAbove(false)
      return
    }
    syncPosition()
  }, [open, placement, align, portal])

  useEffect(() => {
    if (!open || !portal) return
    const onReflow = () => syncPosition()
    window.addEventListener('resize', onReflow)
    window.addEventListener('scroll', onReflow, true)
    return () => {
      window.removeEventListener('resize', onReflow)
      window.removeEventListener('scroll', onReflow, true)
    }
  }, [open, portal, placement, align])

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = () => setOpen(false)

  const vertical = placeAbove ? 'bottom-full mb-1' : 'top-full mt-1'
  const horizontal = align === 'right' ? 'right-0' : 'left-0'
  const panelClass = dense
    ? 'min-w-[9.5rem] max-w-[17rem] py-0.5'
    : 'min-w-[12rem] max-w-[14rem] py-1'

  const showPanel = open
  const panel = showPanel && (
    <div
      ref={menuRef}
      role="menu"
      aria-label={triggerAriaLabel}
      style={
        portal && menuPos
          ? {
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              transform: align === 'right' ? 'translateX(-100%)' : undefined,
              zIndex: 9999,
            }
          : undefined
      }
      className={`${
        portal ? '' : `absolute z-50 ${vertical} ${horizontal}`
      } ${panelClass} max-h-[min(16rem,calc(100vh-1.5rem))] overflow-y-auto bg-paper border border-rule shadow-[0_8px_24px_rgba(18,21,26,0.12)] rounded-lg py-1`}
    >
      {children(close)}
    </div>
  )

  return (
    <MenuDensityContext.Provider value={dense}>
      <div ref={wrapRef} className={`relative inline-block ${className}`}>
        <button
          type="button"
          onClick={toggleOpen}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={triggerAriaLabel}
          className={`inline-flex items-center justify-center text-ink-500 hover:text-ink transition-colors rounded-sm ${triggerClassName}`}
        >
          {trigger}
        </button>
        {!portal && panel}
      </div>
      {portal && panel && createPortal(panel, document.body)}
    </MenuDensityContext.Provider>
  )
}

export const MenuItem: React.FC<{
  onClick: () => void
  children: React.ReactNode
  icon?: React.ReactNode
  danger?: boolean
  disabled?: boolean
  /** Marker for current value — adds a leading tick column. */
  selected?: boolean
  /** Native tooltip for truncated labels. */
  title?: string
}> = ({
  onClick,
  children,
  icon,
  danger = false,
  disabled = false,
  selected = false,
  title,
}) => {
  const dense = useContext(MenuDensityContext)
  const tone = danger
    ? 'text-signal hover:bg-signal-50'
    : 'text-ink hover:bg-paper-200'
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full min-w-0 items-center text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        dense ? 'gap-1.5 px-1.5 py-1 text-xs' : 'gap-2 px-3 py-2 text-sm'
      } ${tone}`}
    >
      {selected ? (
        <span
          aria-hidden
          className={`shrink-0 text-ink-500 ${dense ? 'w-2.5 text-[10px]' : 'w-3 text-xs'}`}
        >
          ✓
        </span>
      ) : icon ? null : (
        <span
          aria-hidden
          className={`shrink-0 ${dense ? 'w-2.5' : 'w-3'}`}
        />
      )}
      {icon && (
        <span className="shrink-0 inline-flex items-center" aria-hidden>
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate" title={title ?? (typeof children === 'string' ? children : undefined)}>
        {children}
      </span>
    </button>
  )
}

export const MenuSeparator: React.FC = () => {
  const dense = useContext(MenuDensityContext)
  return (
    <div
      role="separator"
      className={`border-t border-rule ${dense ? 'my-0.5' : 'my-1'}`}
    />
  )
}

export const MenuLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dense = useContext(MenuDensityContext)
  return (
    <div
      className={`font-semibold text-ink-500 ${
        dense
          ? 'px-1.5 pt-1 pb-0.5 text-[10px]'
          : 'px-3 pt-2 pb-1 text-xs'
      }`}
    >
      {children}
    </div>
  )
}

export default Menu
